import type { ServiceOpeningDay } from '../models/data';
import { buildReservationOccupancyIntervalFromTiming } from './reservationOccupancyInterval.ts';

export type PublicReservationLocale = 'hu' | 'en';

export interface TranslatableLabel {
  label?: string;
  labelEn?: string;
}

export interface TranslatableName {
  name?: string;
  nameEn?: string;
}

export interface TranslatableCustomSelect extends TranslatableLabel {
  options?: string[];
  optionLabelsEn?: Record<string, string>;
}

const hasText = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export const isValidReservationTime = (value: unknown): value is string =>
  typeof value === 'string' && TIME_PATTERN.test(value);

export const resolvePublicLabel = <T extends TranslatableLabel>(
  item: T | null | undefined,
  locale: PublicReservationLocale,
): string => {
  if (!item) return '';
  if (locale === 'en' && hasText(item.labelEn)) return item.labelEn.trim();
  return hasText(item.label) ? item.label.trim() : '';
};

export const resolvePublicName = <T extends TranslatableName>(
  item: T | null | undefined,
  locale: PublicReservationLocale,
): string => {
  if (!item) return '';
  if (locale === 'en' && hasText(item.nameEn)) return item.nameEn.trim();
  return hasText(item.name) ? item.name.trim() : '';
};

export const resolveCustomSelectOptionLabel = (
  field: TranslatableCustomSelect | null | undefined,
  option: string,
  locale: PublicReservationLocale,
): string => {
  if (locale === 'en') {
    const translated = field?.optionLabelsEn?.[option];
    if (hasText(translated)) return translated.trim();
  }
  return option;
};

export const resolveReservationEndDateTime = (
  dateKey: string,
  startTime: string,
  endTime?: string | null,
): Date => {
  if (!isValidReservationTime(startTime)) return new Date(Number.NaN);
  const start = new Date(`${dateKey}T${startTime}`);
  if (Number.isNaN(start.getTime())) return new Date(Number.NaN);
  if (endTime) {
    if (!isValidReservationTime(endTime)) return new Date(Number.NaN);
    return new Date(`${dateKey}T${endTime}`);
  }
  const interval = buildReservationOccupancyIntervalFromTiming({
    startTime: start,
    defaultDurationMinutes: 120,
    defaultBufferMinutes: 0,
  });
  return interval.ok ? interval.interval.guestEnd : new Date(Number.NaN);
};


const SERVICE_OPENING_DAY_LABELS: Record<PublicReservationLocale, readonly string[]> = {
  hu: ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'],
  en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
};

export const formatPublicServiceOpeningDays = (
  days: ServiceOpeningDay[] | undefined,
  locale: PublicReservationLocale,
): string => {
  if (!days || days.length === 0 || days.length === 7) return '';
  const labels = SERVICE_OPENING_DAY_LABELS[locale];
  const sortedDays = Array.from(new Set(days)).sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = sortedDays[0];
  let previous = sortedDays[0];

  for (let index = 1; index <= sortedDays.length; index += 1) {
    const current = sortedDays[index];
    if (current === previous + 1) {
      previous = current;
      continue;
    }

    ranges.push(start === previous ? labels[start] : `${labels[start]}–${labels[previous]}`);
    start = current;
    previous = current;
  }

  return ranges.join(', ');
};

export const isReservationFormSubmittable = (input: {
  name?: string;
  phone?: string;
  email?: string;
  startTime?: string;
  endTime?: string;
}): boolean => {
  if (!hasText(input.name) || !hasText(input.phone) || !hasText(input.email) || !isValidReservationTime(input.startTime)) {
    return false;
  }
  if (!input.endTime) return true;
  return isValidReservationTime(input.endTime) && input.endTime > input.startTime;
};

